import { useState } from 'preact/hooks'
import { AlertTriangle, Check, Hourglass, Image as ImageIcon, Info, Loader2, RotateCcw, Trash2, Upload } from 'lucide-preact'
import type { WebViewDto } from '@/api/webs'
import { webinarRoomApi } from '@/api/webinarRoom'
import { CloudFilePicker } from '@/components/common/upload/CloudFilePicker'
import type { CloudProvider } from '@/components/common/upload/cloudFile'
import { useToast } from '@/hooks/useToast'
import { useWorkspaceCapabilities } from '@/hooks/useWorkspaceCapabilities'
import { retryOnStaleETag } from '@/utils/etagRetry'
import { ImageDropZone } from './ImageDropZone'
import { RoomWaitingPreview, type RoomBrand } from './RoomWaitingPreview'
import { DropZone, Progress, ReplacementStrip, SourceIcon, type ReplacementCopy } from './RoomUploadParts'
import {
    LOCK_TITLE, PREPARING_TOO_LONG, ROOM_RECORDING_MAX_BYTES, cloudSourceOf, failedFacts, leaveNote, preparingTooLong, recordingFacts,
    replacementBusy, replacementView, transferText, uploadReplaces, uploadedByLine,
} from './roomRecording'
import { STALLED_NOTE, waitingText } from './roomUpload'
import {
    WAITING_VIDEO_FIELD_NAME, WAITING_VIDEO_LIMITS, waitingImageFacts, waitingImageFileProblem, waitingSubLine, waitingView,
} from './roomWaiting'
import { useImageSize } from './useImageSize'
import { importRequestOf, useRoomVideoUpload } from './useRoomVideoUpload'
import { BUTTON, BUTTON_GHOST, CARD, CARD_SUB, CARD_TITLE, NOTE, PILL_READY, PILL_WARNING, STRIP_ERROR, STRIP_WARNING } from './roomStyles'

interface RoomWaitingVideoCardProps {
    web: WebViewDto
    isSignedOff: boolean
    /** The client's colours and logo, as the room page draws the waiting screen in them. */
    brand: RoomBrand
    /** Rereads the web, after every write here and whenever the server says the video moved on. */
    reload: () => Promise<void>
    onPatchWeb: (patcher: (prev: WebViewDto) => WebViewDto) => void
}

const uploadKey = (webId: string) => `webinar-room-waiting-video:${webId}`

const REPLACEMENT_COPY: ReplacementCopy = {
    failedHeading: fileName => `Couldn’t prepare ${fileName}.`,
    currentKept: 'The current video keeps playing.',
    preparing: 'Uploaded. Preparing it, which usually takes a few minutes. The current video keeps playing until then.',
    stuck: 'The current video keeps playing.',
    uploading: 'The current video keeps playing until this one is ready.',
    keepCurrent: 'Keep the current video',
}

interface PlaybackChoiceProps {
    playsOnce: boolean
    disabled: boolean
    title?: string
    onChange: (playsOnce: boolean) => void
}

function PlaybackChoice({ playsOnce, disabled, title, onChange }: PlaybackChoiceProps) {
    const option = (value: boolean, label: string) => (
        <button
            type="button"
            role="radio"
            aria-checked={playsOnce === value}
            disabled={disabled}
            title={title}
            onClick={() => onChange(value)}
            className={`px-3 py-1.5 rounded-md text-sm transition-colors disabled:cursor-not-allowed ${playsOnce === value
                ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white font-medium shadow-sm'
                : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'}`}
        >
            {label}
        </button>
    )
    return (
        <div role="radiogroup" aria-label="How it plays" className="inline-flex gap-0.5 p-0.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-900/60">
            {option(false, 'Loop until the start')}
            {option(true, 'Play once')}
        </div>
    )
}

/**
 * The Room tab's While viewers wait card (ORB-1118): the waiting screen as viewers see it, and an optional video to
 * play behind its countdown, uploaded like the recording. A ready one keeps playing while its replacement comes in,
 * as the recording does. It loops until the start or plays once. The pill carries the server's
 * `OnlineEvent.WaitingVideo` sign-off item word for word. An image can stand in for the video, never beside it, so both
 * are offered only while neither is set; it is ready as soon as it uploads, and a replacement takes its place at once.
 */
export function RoomWaitingVideoCard({ web, isSignedOff, brand, reload, onPatchWeb }: RoomWaitingVideoCardProps) {
    const { canEdit } = useWorkspaceCapabilities()
    const { showErrorUnless422 } = useToast()
    const event = web.OnlineEvent!
    const video = event.WaitingVideo ?? null
    const pending = event.PendingWaitingVideo ?? null
    const playsOnce = event.WaitingVideoPlaysOnce === true
    const image = event.WaitingImage ?? null
    const imageSize = useImageSize(image?.Url ?? null)
    const item = web.OutstandingForSignOff?.find(error => error.FieldName === WAITING_VIDEO_FIELD_NAME) ?? null

    const [chooserOpen, setChooserOpen] = useState(false)
    const [cloudProvider, setCloudProvider] = useState<CloudProvider | null>(null)
    const [imageChooserOpen, setImageChooserOpen] = useState(false)
    const [imageError, setImageError] = useState<string | null>(null)
    // The name of the image this browser is sending, held until the web is reread so the card never flashes empty.
    const [imageUploading, setImageUploading] = useState<string | null>(null)

    const workspaceId = web.WorkspaceId
    const webId = web.Id
    const editable = canEdit && !isSignedOff
    const lockTitle = isSignedOff ? LOCK_TITLE : undefined

    const closeChooser = () => {
        setChooserOpen(false)
        setCloudProvider(null)
        setFileError(null)
    }

    const removeVideo = async (videoId: string) => {
        const res = await webinarRoomApi.removeWaitingVideo(workspaceId, webId, videoId)
        if (res.error) showErrorUnless422('Not removed', res, 'The waiting video could not be removed.')
    }

    const { upload, busy, setBusy, fileError, setFileError, refetchWeb, uploadFromComputer, importFromCloud, cancelUpload, retry } = useRoomVideoUpload({
        web,
        uploadKey: uploadKey(webId),
        what: 'waiting video',
        reload,
        onPatchWeb,
        beginUpload: (document, file) => webinarRoomApi.beginWaitingVideoUpload(workspaceId, webId, {
            ETag: document.ETag, FileName: file.name, SizeBytes: file.size, ContentType: file.contentType,
        }),
        beginImport: (document, file) => webinarRoomApi.importWaitingVideo(workspaceId, webId, { ETag: document.ETag, ...importRequestOf(file) }),
        abandon: removeVideo,
        onStarted: closeChooser,
    })
    const uploadRunning = upload?.status === 'uploading' || upload?.status === 'finishing'
    const replacing = uploadReplaces(video)
    const view = waitingView(video, uploadRunning && !replacing)
    const replacement = replacementView(pending, uploadRunning && replacing)
    const replacementOnItsWay = replacementBusy(replacement)
    const now = new Date()
    const stuck = view === 'preparing' && preparingTooLong(video, now)
    const removeOrCancel = () => cancelUpload(video?.Id ?? null)
    const cancelReplacement = () => cancelUpload(pending?.Id ?? null)

    const choosePlayback = async (next: boolean) => {
        if (next === playsOnce) return
        setBusy(true)
        const res = await retryOnStaleETag(document => webinarRoomApi.setWaitingVideoPlayback(document.WorkspaceId, document.Id, {
            ETag: document.ETag, PlaysOnce: next,
        }), web, refetchWeb).finally(() => setBusy(false))
        if (res.data) {
            const saved = res.data
            onPatchWeb(prev => ({ ...prev, ...saved }))
        } else {
            showErrorUnless422('Not saved', res, 'How the waiting video plays could not be saved.')
        }
    }

    const closeImageChooser = () => {
        setImageChooserOpen(false)
        setImageError(null)
    }

    const uploadImage = async (file: File) => {
        const problem = waitingImageFileProblem(file)
        if (problem) { setImageError(problem); return }
        closeImageChooser()
        setImageUploading(file.name)
        try {
            const res = await webinarRoomApi.uploadWaitingImage(workspaceId, webId, file)
            if (res.error) showErrorUnless422('Not uploaded', res, 'The image could not be uploaded.')
        } finally {
            await reload()
            setImageUploading(null)
        }
    }

    const removeImage = async () => {
        setBusy(true)
        const res = await webinarRoomApi.removeWaitingImage(workspaceId, webId)
        setBusy(false)
        if (res.error) showErrorUnless422('Not removed', res, 'The waiting image could not be removed.')
        await reload()
    }

    const pill = item
        ? <span className={PILL_WARNING} title={item.Message}><AlertTriangle className="w-3 h-3" />Needed before sign-off</span>
        : video?.State === 'Ready' || image ? <span className={PILL_READY}><Check className="w-3 h-3" />Ready</span> : null

    const imageChooser = (
        <div className="mt-4">
            <ImageDropZone disabled={busy} onFile={file => void uploadImage(file)} />
            {imageError && <p role="alert" className="mt-3 text-sm text-red-600 dark:text-red-400">{imageError}</p>}
            <div className="mt-2.5 flex justify-end">
                <button type="button" className={BUTTON_GHOST} onClick={closeImageChooser}>{image ? 'Keep this image' : 'Cancel'}</button>
            </div>
        </div>
    )

    const chooser = (
        <div className="mt-4">
            <DropZone what="video" limits={WAITING_VIDEO_LIMITS} disabled={busy} onFile={file => void uploadFromComputer(file)} onCloud={provider => { setFileError(null); setCloudProvider(provider) }} />
            {fileError && <p role="alert" className="mt-3 text-sm text-red-600 dark:text-red-400">{fileError}</p>}
            {cloudProvider && (
                <div className="mt-4 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                    <CloudFilePicker enabled provider={cloudProvider} onProviderChange={provider => setCloudProvider(provider)} maxBytes={ROOM_RECORDING_MAX_BYTES} onPicked={file => void importFromCloud(file)} />
                    <div className="mt-4 flex items-center justify-end gap-3">
                        {busy && <Loader2 className="w-4 h-4 animate-spin text-slate-400" aria-label="Starting the import" />}
                        <button type="button" className={BUTTON_GHOST} onClick={() => setCloudProvider(null)}>Cancel</button>
                    </div>
                </div>
            )}
            <div className="mt-2.5 flex justify-end">
                <button type="button" className={BUTTON_GHOST} onClick={closeChooser}>{video ? 'Keep this video' : 'Cancel'}</button>
            </div>
        </div>
    )

    // Held while a replacement is on its way: Cancel there drops it, and the upload this browser runs is the replacement's.
    const removeButton = canEdit && (
        <button
            type="button"
            className={BUTTON_GHOST}
            disabled={!editable || busy || replacementOnItsWay}
            title={lockTitle ?? (replacementOnItsWay ? 'Cancel the replacement first' : undefined)}
            onClick={() => void removeOrCancel()}
        >
            <Trash2 className="w-3.5 h-3.5" />Remove
        </button>
    )

    const body = (() => {
        if (imageUploading) {
            return (
                <div className="mt-4 grid grid-cols-[36px_minmax(0,1fr)] gap-3.5 items-center" role="status">
                    <span className="w-9 h-9 rounded-lg grid place-items-center bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 text-slate-400">
                        <ImageIcon className="w-4 h-4" />
                    </span>
                    <div className="min-w-0">
                        <p className="font-medium text-slate-900 dark:text-white truncate">{imageUploading}</p>
                        <Progress fraction={null} />
                        <p className="mt-1.5 text-sm text-slate-500 dark:text-slate-400">Uploading the image.</p>
                    </div>
                </div>
            )
        }

        if (image) {
            return (
                <div className="mt-4 grid gap-4">
                    <div className="min-w-0">
                        <p className="font-medium text-slate-900 dark:text-white truncate">{image.FileName}</p>
                        <p className="text-sm tabular-nums text-slate-500 dark:text-slate-400">{waitingImageFacts(image, imageSize, now)}</p>
                    </div>
                    {canEdit && !imageChooserOpen && (
                        <div className="flex flex-wrap justify-end gap-2">
                            <button type="button" className={BUTTON} disabled={!editable || busy} title={lockTitle} onClick={() => setImageChooserOpen(true)}>
                                <Upload className="w-3.5 h-3.5" />Replace
                            </button>
                            <button type="button" className={BUTTON_GHOST} disabled={!editable || busy} title={lockTitle} onClick={() => void removeImage()}>
                                <Trash2 className="w-3.5 h-3.5" />Remove
                            </button>
                        </div>
                    )}
                    {imageChooserOpen && imageChooser}
                </div>
            )
        }

        if (view === 'empty') {
            if (chooserOpen) return chooser
            if (imageChooserOpen) return imageChooser
            return (
                <div className="mt-4 flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
                    <p className="text-sm text-slate-600 dark:text-slate-300 max-w-[52ch]">Add a short video or an image to show behind the countdown: a promo, a teaser, a sponsor spot or a slide.</p>
                    {canEdit && (
                        <div className="flex flex-wrap gap-2">
                            <button type="button" className={BUTTON} disabled={!editable || busy} title={lockTitle} onClick={() => setChooserOpen(true)}>
                                <Upload className="w-3.5 h-3.5" />Add a video
                            </button>
                            <button type="button" className={BUTTON} disabled={!editable || busy} title={lockTitle} onClick={() => setImageChooserOpen(true)}>
                                <ImageIcon className="w-3.5 h-3.5" />Add an image
                            </button>
                        </div>
                    )}
                </div>
            )
        }

        if (view === 'uploading') {
            const source = upload ? null : cloudSourceOf(video)
            const fraction = upload && upload.totalBytes > 0 ? upload.uploadedBytes / upload.totalBytes : null
            return (
                <div className="mt-4">
                    <div className="grid grid-cols-[36px_minmax(0,1fr)_auto] gap-3.5 items-center">
                        <span className="w-9 h-9 rounded-lg grid place-items-center bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 text-slate-400">
                            <SourceIcon source={source} className="w-4 h-4" />
                        </span>
                        <div className="min-w-0">
                            <p className="font-medium text-slate-900 dark:text-white truncate">{upload?.fileName ?? video?.FileName}</p>
                            <Progress fraction={fraction} />
                            <p className="mt-1.5 text-sm tabular-nums text-slate-500 dark:text-slate-400">
                                {upload ? transferText(upload.uploadedBytes, upload.totalBytes, upload.secondsLeft, true) : waitingText(video, source)}
                            </p>
                        </div>
                        {canEdit && <button type="button" className={BUTTON} disabled={busy} onClick={() => void removeOrCancel()}>Cancel</button>}
                    </div>
                    <p className={NOTE}>
                        <Info className="w-3.5 h-3.5 mt-0.5 flex-none text-slate-400" />
                        <span>{upload || source ? leaveNote(source) : STALLED_NOTE}</span>
                    </p>
                </div>
            )
        }

        const current = video!
        const choice = (
            <PlaybackChoice playsOnce={playsOnce} disabled={!editable || busy} title={lockTitle} onChange={next => void choosePlayback(next)} />
        )

        if (view === 'preparing' && !stuck) {
            return (
                <div className="mt-4 grid gap-4">
                    <div className="grid grid-cols-[36px_minmax(0,1fr)_auto] gap-3.5 items-center">
                        <span className="w-9 h-9 rounded-lg grid place-items-center bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 text-slate-400">
                            <Loader2 className="w-4 h-4 animate-spin" />
                        </span>
                        <div className="min-w-0">
                            <p className="font-medium text-slate-900 dark:text-white truncate">{current.FileName}</p>
                            <p className="text-sm text-slate-500 dark:text-slate-400">Uploaded. Preparing it, which usually takes a few minutes.</p>
                        </div>
                        {removeButton}
                    </div>
                    {choice}
                </div>
            )
        }

        if (view === 'failed' || stuck) {
            const failed = view === 'failed'
            const problem = stuck ? PREPARING_TOO_LONG : current.FailureMessage?.trim() || item?.Message
            return (
                <div className="mt-4">
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                        <p className="min-w-0 flex-1 font-medium text-slate-900 dark:text-white truncate">{current.FileName}</p>
                        <p className="text-sm text-slate-500 dark:text-slate-400">{failedFacts(current, now)}</p>
                    </div>
                    {problem && (
                        <div role={failed ? 'alert' : 'status'} className={failed ? STRIP_ERROR : STRIP_WARNING}>
                            <AlertTriangle className={`w-4 h-4 mt-0.5 flex-none ${failed ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`} />
                            <div>
                                <p>{problem}</p>
                                {canEdit && (
                                    <div className="mt-2.5 flex flex-wrap gap-2">
                                        <button type="button" className={BUTTON} disabled={!editable || busy} title={lockTitle} onClick={() => void retry(current.Id)}>
                                            <RotateCcw className="w-3.5 h-3.5" />Try again
                                        </button>
                                        <button type="button" className={BUTTON_GHOST} disabled={!editable || busy} title={lockTitle} onClick={() => setChooserOpen(true)}>Upload a different file</button>
                                        {removeButton}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    {chooserOpen && chooser}
                </div>
            )
        }

        const uploadedBy = uploadedByLine(current, now)
        return (
            <div className="mt-4 grid gap-4">
                <div className="min-w-0">
                    <p className="font-medium text-slate-900 dark:text-white truncate">{current.FileName}</p>
                    <p className="text-sm tabular-nums text-slate-500 dark:text-slate-400">{recordingFacts(current)}</p>
                    {uploadedBy && <p className="text-sm text-slate-500 dark:text-slate-400">{uploadedBy}</p>}
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3">
                    {choice}
                    {canEdit && !chooserOpen && (
                        <div className="flex flex-wrap gap-2">
                            <button
                                type="button"
                                className={BUTTON}
                                disabled={!editable || busy || replacementOnItsWay}
                                title={lockTitle ?? (replacementOnItsWay ? 'A replacement is already on its way' : undefined)}
                                onClick={() => setChooserOpen(true)}
                            >
                                <Upload className="w-3.5 h-3.5" />Replace
                            </button>
                            {removeButton}
                        </div>
                    )}
                </div>
                {replacement && (
                    <ReplacementStrip
                        view={replacement}
                        pending={pending}
                        upload={upload && replacing ? upload : null}
                        copy={REPLACEMENT_COPY}
                        now={now}
                        canEdit={canEdit}
                        locked={!editable}
                        lockTitle={lockTitle}
                        busy={busy}
                        onCancel={cancelReplacement}
                        onRetry={retry}
                        onUploadDifferent={() => setChooserOpen(true)}
                    />
                )}
                {chooserOpen && !replacementOnItsWay && chooser}
            </div>
        )
    })()

    return (
        <section className={CARD} aria-labelledby="room-waiting-title">
            <div className="flex items-center justify-between gap-3">
                <h2 id="room-waiting-title" className={CARD_TITLE}><Hourglass className="w-5 h-5 text-primary-600" /> While viewers wait</h2>
                {pill}
            </div>
            <p className={CARD_SUB}>{waitingSubLine(video ? 'video' : image ? 'image' : 'slate', playsOnce)}</p>
            <div className="mt-5">
                <RoomWaitingPreview workspaceId={workspaceId} webId={webId} event={event} brand={brand} />
                {body}
            </div>
        </section>
    )
}
